// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminDashboard from './AdminDashboard';
import {
  Briefcase, TrendingUp, TrendingDown, Award, Clock,
  FileText, Users, Star, Trash2, RefreshCw,
  CheckCircle, AlertCircle, Play, Eye, ChevronRight,
  Layers, Target, Inbox, ArrowRight, Calendar,
} from 'lucide-react';
import api from '../services/api';
import { kanbanAPI, aoAPI } from '../services/api';
import { initials, getRoleName } from '../utils/helpers';
import toast from 'react-hot-toast';

import { extractError } from '../context/AuthContext';
const PIE_COLORS  = ['#3B82F6', '#22C55E', '#DC2626', '#F59E0B', '#A78BFA', '#06B6D4'];

const STATUS_META = {
  NOUVEAU:                            { label:'Nouveau',           color:'#3B82F6', bg:'rgba(59,130,246,0.08)' },
  EN_COURS_DE_TRAITEMENT:             { label:'En cours',          color:'#F59E0B', bg:'#FFFBEB' },
  ENVOYE_A_EQUIPE_TALENT_ACQUISITION: { label:'Envoyé TA',         color:'#A78BFA', bg:'rgba(139,92,246,0.10)' },
  EN_COURS_DE_SOURCING:               { label:'En sourcing',       color:'#06B6D4', bg:'rgba(8,145,178,0.10)' },
  SHORT_LIST_ENVOYEE:                 { label:'Shortlist envoyée', color:'#10B981', bg:'#ECFDF5' },
  TOP_PROFIL_ENVOYE_AU_CLIENT:        { label:'Top profil envoyé', color:'#22C55E', bg:'rgba(34,197,94,0.08)' },
  REPONDU:                            { label:'Répondu',           color:'#0EA5E9', bg:'#F0F9FF' },
  GAGNE:                              { label:'Gagné',             color:'#16A34A', bg:'rgba(34,197,94,0.08)' },
  PERDU:                              { label:'Perdu',             color:'#DC2626', bg:'rgba(239,68,68,0.12)' },
};

// ─────────────────────────────────────────────────────────────
// Routeur
// ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const role = (user?.role || '').toUpperCase();
  if (role === 'BUSINESS_MANAGER')   return <IADashboard />;
  if (role === 'TALENT_ACQUISITION') return <TADashboard />;
  return <AdminDashboard />;
}

// ─────────────────────────────────────────────────────────────
// Dashboard IA
// ─────────────────────────────────────────────────────────────
function IADashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading,   setLoading]   = useState(true);
  const [myAOs,     setMyAOs]     = useState([]);
  const [newAOs,    setNewAOs]    = useState([]);
  const [activity,  setActivity]  = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mineRes, newRes, actRes] = await Promise.all([
        api.get('/ao-management/mine'),
        api.get('/ao-management/nouveaux'),
        api.get('/ao-management/my-activity'),
      ]);
      setMyAOs(mineRes.data || []);
      setNewAOs(newRes.data || []);
      setActivity(actRes.data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const kpis = [
    { label:'Mes AO en cours',     value: myAOs.filter(a => a.ao_avancement === 'EN_COURS_DE_TRAITEMENT').length,                                                                   icon:TrendingUp,  color:'#3B82F6', bg:'rgba(59,130,246,0.08)', border:'rgba(59,130,246,0.28)' },
    { label:'Nouveaux AO',         value: newAOs.length,                                                                                                                             icon:Inbox,       color:'#7C3AED', bg:'rgba(139,92,246,0.10)', border:'rgba(139,92,246,0.28)' },
    { label:'Shortlists envoyées', value: myAOs.filter(a => ['SHORT_LIST_ENVOYEE','TOP_PROFIL_ENVOYE_AU_CLIENT','REPONDU','GAGNE'].includes(a.ao_avancement)).length,                icon:CheckCircle, color:'#059669', bg:'#ECFDF5', border:'rgba(34,197,94,0.25)' },
    { label:'Top profils envoyés', value: myAOs.filter(a => ['TOP_PROFIL_ENVOYE_AU_CLIENT','REPONDU','GAGNE'].includes(a.ao_avancement)).length,                                     icon:Star,        color:'#EA580C', bg:'#FFF7ED', border:'rgba(234,88,12,0.25)' },
    { label:'AO gagnés',           value: myAOs.filter(a => a.ao_avancement === 'GAGNE').length,                                                                                     icon:Award,       color:'#16A34A', bg:'rgba(34,197,94,0.08)', border:'rgba(34,197,94,0.35)' },
    { label:'Total mes AO',        value: myAOs.length,                                                                                                                              icon:Layers,      color:'#0F172A', bg:'var(--bg-elevated)', border:'#E2E8F0' },
  ];

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Mon espace</h1>
          <p>Bonjour {user?.prenom} — voici vos appels d'offres et activités du jour</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/> Actualiser</button>
          <button className="btn btn-primary" onClick={() => navigate('/kanban')}><Layers size={15}/> Pipeline AO</button>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14, marginBottom:28 }}>
        {kpis.map(({ label, value, icon:Icon, color, bg, border }) => (
          <div key={label} style={{ background:bg, border:`1px solid ${border}`, borderRadius:12, padding:'18px 20px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:`${color}22`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Icon size={17} color={color}/>
              </div>
              <span style={{ fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</span>
            </div>
            <div style={{ fontSize:'2rem', fontWeight:800, color, lineHeight:1 }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title"><Target size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>Mes AO affectés ({myAOs.length})</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/kanban')}>Voir tout <ChevronRight size={13}/></button>
          </div>
          {myAOs.length === 0 ? (
            <div className="empty-state" style={{ padding:'32px 0' }}><Target size={28}/><p style={{ fontSize:'0.875rem' }}>Aucun AO en cours</p></div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column' }}>
              {myAOs.slice(0,8).map(ao => <AORow key={ao.id} ao={ao} onClick={() => navigate(`/ao/${ao.id}`)}/>)}
              {myAOs.length > 8 && (
                <button className="btn btn-ghost btn-sm" style={{ margin:'8px 0', color:'var(--text-muted)' }} onClick={() => navigate('/kanban')}>
                  + {myAOs.length - 8} autres AO
                </button>
              )}
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-header">
            <span className="card-title"><Inbox size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>Nouveaux AO à traiter ({newAOs.length})</span>
          </div>
          {newAOs.length === 0 ? (
            <div className="empty-state" style={{ padding:'32px 0' }}><Inbox size={28}/><p style={{ fontSize:'0.875rem' }}>Aucun nouvel AO disponible</p></div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column' }}>
              {newAOs.slice(0,8).map(ao => <AORow key={ao.id} ao={ao} isNew onClick={() => navigate(`/ao/${ao.id}`)}/>)}
              {newAOs.length > 8 && (
                <button className="btn btn-ghost btn-sm" style={{ margin:'8px 0', color:'var(--text-muted)' }} onClick={() => navigate('/kanban')}>
                  + {newAOs.length - 8} autres AO
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <ActivityTable events={activity} navigate={navigate}/>
    </div>
  );
}

const ACTIVITY_ICONS = {
  AO_STATUS:  { color:'#3B82F6', label:'AO' },
  EMAIL:      { color:'#0891b2', label:'Email' },
  ENTRETIEN:  { color:'#059669', label:'Entretien' },
  NOTE:       { color:'#7c3aed', label:'Note' },
  APPEL:      { color:'#d97706', label:'Appel' },
  RELANCE:    { color:'#dc2626', label:'Relance' },
};

function ActivityTable({ events, navigate }) {
  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }) + ' ' +
           d.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
  };

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title"><Calendar size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>Historique de mes actions</span>
        <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{events.length} entrées</span>
      </div>
      {events.length === 0 ? (
        <div className="empty-state" style={{ padding:'32px 0' }}>
          <Calendar size={28}/><p style={{ fontSize:'0.875rem' }}>Aucune activité enregistrée</p>
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--border)' }}>
                {['Type','Action','AO / Candidat','Date'].map(h => (
                  <th key={h} style={{ padding:'8px 16px', textAlign:'left', fontWeight:600, color:'var(--text-muted)', fontSize:'0.75rem', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => {
                const meta = ACTIVITY_ICONS[ev.type] || { color:'var(--text-muted)', label: ev.type };
                const subject = ev.type === 'AO_STATUS'
                  ? <span>{ev.ao_title || '—'}{ev.ao_client ? <span style={{ color:'var(--text-muted)', marginLeft:6 }}>{ev.ao_client}</span> : null}</span>
                  : <span>{ev.candidate_name || '—'}{ev.action_title ? <span style={{ color:'var(--text-muted)', marginLeft:6 }}>— {ev.action_title}</span> : null}</span>;
                return (
                  <tr
                    key={i}
                    style={{ borderBottom:'1px solid var(--border)', cursor: ev.ao_id ? 'pointer' : 'default' }}
                    onClick={() => ev.ao_id && navigate(`/ao/${ev.ao_id}`)}
                  >
                    <td style={{ padding:'10px 16px' }}>
                      <span style={{ whiteSpace:'nowrap', padding:'2px 8px', borderRadius:20, fontSize:'0.6875rem', fontWeight:700, background:`${meta.color}18`, color:meta.color, border:`1px solid ${meta.color}33` }}>
                        {meta.label}
                      </span>
                    </td>
                    <td style={{ padding:'10px 16px', color:'var(--text-primary)', fontWeight:500 }}>{ev.label}</td>
                    <td style={{ padding:'10px 16px', color:'var(--text-secondary)' }}>{subject}</td>
                    <td style={{ padding:'10px 16px', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{fmt(ev.date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Dashboard Talent Acquisition
// ─────────────────────────────────────────────────────────────
const TA_ACTIVE_STATUSES  = new Set(['SOURCING', 'EN_COURS_DE_SOURCING']);
const TA_CLOSED_STATUSES  = new Set(['SHORT_LIST_ENVOYEE', 'TOP_PROFIL_ENVOYE_AU_CLIENT', 'REPONDU', 'GAGNE', 'PERDU', 'ANNULE', 'TERMINE', 'SUPPRIME']);

function TADashboard() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const myId      = String(user?.id || '');

  const [loading,     setLoading]     = useState(true);
  const [taking,      setTaking]      = useState(null); // ao_id en cours de prise
  const [allTeamAOs,  setAllTeamAOs]  = useState([]);
  const [taMembers,   setTaMembers]   = useState([]);
  const [selectedTA,  setSelectedTA]  = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const boardRes = await kanbanAPI.getBoard({});
      const cols     = boardRes.data?.columns || [];

      const getCol = (status) =>
        (cols.find(c => (c.status || '').toUpperCase() === status.toUpperCase())?.items || []);

      const enAttente  = getCol('ENVOYE_A_EQUIPE_TALENT_ACQUISITION');
      const sourcing   = getCol('SOURCING');
      const enSourcing = getCol('EN_COURS_DE_SOURCING');
      const shortlists = getCol('SHORT_LIST_ENVOYEE');
      const topProfil  = getCol('TOP_PROFIL_ENVOYE_AU_CLIENT');
      const repondu    = getCol('REPONDU');
      const gagne      = getCol('GAGNE');
      const perdu      = getCol('PERDU');
      const annule     = getCol('ANNULE');
      const termine    = getCol('TERMINE');

      const teamAOs = [
        ...enAttente, ...sourcing, ...enSourcing,
        ...shortlists, ...topProfil, ...repondu,
        ...gagne, ...perdu, ...annule, ...termine,
      ];

      // Membres TA uniques présents dans les AOs actifs
      const taMap = {};
      [...sourcing, ...enSourcing, ...shortlists].forEach(ao => {
        if (ao.talent_acquisition_id && ao.talent_acquisition_name) {
          taMap[String(ao.talent_acquisition_id)] = ao.talent_acquisition_name;
        }
      });

      setAllTeamAOs(teamAOs);
      setTaMembers(Object.entries(taMap).map(([id, name]) => ({ id, name })));
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleTakeSourcing = async (ao, e) => {
    e.stopPropagation();
    const aoId = String(ao.ao_id || ao.id);
    setTaking(aoId);
    try {
      await aoAPI.takeSourcing(aoId);
      toast.success(`AO "${ao.job_title}" ajouté à vos missions`);
      await fetchData();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setTaking(null);
    }
  };

  // Partitions selon mon ID
  const availableAOs  = allTeamAOs.filter(ao =>
    (ao.ao_avancement || '').toUpperCase() === 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION'
    && !ao.talent_acquisition_id   // pas encore assigné à un TA spécifique
  );
  const myActiveAOs   = allTeamAOs.filter(ao =>
    String(ao.talent_acquisition_id) === myId
    && (ao.ao_avancement || '').toUpperCase() === 'EN_COURS_DE_SOURCING'
  );
  const myClosedAOs   = allTeamAOs.filter(ao =>
    String(ao.talent_acquisition_id) === myId
    && TA_CLOSED_STATUSES.has((ao.ao_avancement || '').toUpperCase())
  );

  const filteredTeam = selectedTA
    ? allTeamAOs.filter(ao =>
        String(ao.talent_acquisition_id) === selectedTA
        || String(ao.handler_user_id) === selectedTA
      )
    : allTeamAOs;

  const kpis = [
    { label:'AOs en attente',       value: availableAOs.length,              icon:AlertCircle, color:'#F59E0B', bg:'#FFFBEB', border:'#FCD34D' },
    { label:'Mes missions actives', value: myActiveAOs.length,               icon:Play,        color:'#3B82F6', bg:'rgba(59,130,246,0.08)', border:'rgba(59,130,246,0.28)' },
    { label:'Shortlists envoyées',  value: myClosedAOs.filter(a => ['SHORT_LIST_ENVOYEE','TOP_PROFIL_ENVOYE_AU_CLIENT','REPONDU','GAGNE'].includes((a.ao_avancement||'').toUpperCase())).length,
                                                                              icon:CheckCircle, color:'#059669', bg:'#ECFDF5', border:'rgba(34,197,94,0.25)' },
    { label:'Total AOs équipe',     value: allTeamAOs.length,                icon:Layers,      color:'#7C3AED', bg:'rgba(139,92,246,0.10)', border:'rgba(139,92,246,0.28)' },
  ];

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Dashboard Talent Acquisition</h1>
          <p>Bonjour {user?.prenom} — activité de votre équipe sourcing</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/> Actualiser</button>
          <button className="btn btn-primary" onClick={() => navigate('/kanban')}><Target size={15}/> Mes missions</button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:14, marginBottom:28 }}>
        {kpis.map(({ label, value, icon:Icon, color, bg, border }) => (
          <div key={label} style={{ background:bg, border:`1px solid ${border}`, borderRadius:12, padding:'18px 20px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:`${color}22`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Icon size={17} color={color}/>
              </div>
              <span style={{ fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</span>
            </div>
            <div style={{ fontSize:'2rem', fontWeight:800, color, lineHeight:1 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Disponibles + Mes missions actives */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
        {/* AOs disponibles (équipe, sans TA) */}
        <div className="card">
          <div className="card-header">
            <span className="card-title"><Inbox size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>
              AOs disponibles à prendre ({availableAOs.length})
            </span>
          </div>
          {availableAOs.length === 0 ? (
            <div className="empty-state" style={{ padding:'28px 0' }}>
              <Inbox size={26}/><p style={{ fontSize:'0.875rem' }}>Aucun AO en attente d'un TA</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column' }}>
              {availableAOs.slice(0, 8).map(ao => {
                const aoId = String(ao.ao_id || ao.id);
                return (
                  <div key={aoId}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
                      borderBottom:'1px solid var(--border)', cursor:'pointer' }}
                    onClick={() => navigate(`/ao/${aoId}`)}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {ao.job_title || 'Sans titre'}
                      </div>
                      <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>
                        {ao.client || '—'}
                        {ao.response_deadline && <span style={{ marginLeft:8, color:'#EA580C' }}>· délai {ao.response_deadline}</span>}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={taking === aoId}
                      onClick={(e) => handleTakeSourcing(ao, e)}
                      style={{ flexShrink:0, whiteSpace:'nowrap' }}>
                      {taking === aoId ? '…' : <><Play size={12}/> Prendre en charge</>}
                    </button>
                  </div>
                );
              })}
              {availableAOs.length > 8 && (
                <div style={{ padding:'8px 16px', fontSize:'0.75rem', color:'var(--text-muted)' }}>
                  + {availableAOs.length - 8} autres AOs disponibles
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mes missions actives */}
        <div className="card">
          <div className="card-header">
            <span className="card-title"><Target size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>
              Mes missions actives ({myActiveAOs.length})
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/kanban')}>
              Voir tout <ChevronRight size={13}/>
            </button>
          </div>
          {myActiveAOs.length === 0 ? (
            <div className="empty-state" style={{ padding:'28px 0' }}>
              <Target size={26}/><p style={{ fontSize:'0.875rem' }}>Aucune mission active</p>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column' }}>
              {myActiveAOs.slice(0, 8).map(ao => (
                <AORow key={String(ao.ao_id || ao.id)} ao={ao} onClick={() => navigate(`/ao/${String(ao.ao_id || ao.id)}`)}/>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tableau équipe TA */}
      <div className="card section">
        <div className="card-header">
          <span className="card-title">
            <Users size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>
            Activité de l'équipe TA ({filteredTeam.length})
          </span>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {taMembers.length > 0 && (
              <select className="filter-select" value={selectedTA} onChange={e => setSelectedTA(e.target.value)}>
                <option value="">Tous les membres TA</option>
                {taMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/kanban')}>
              Voir mes missions <ChevronRight size={13}/>
            </button>
          </div>
        </div>
        {filteredTeam.length === 0 ? (
          <div className="empty-state" style={{ padding:'32px 0' }}>
            <Layers size={28}/><br/><p style={{ fontSize:'0.875rem' }}>Aucun AO dans le pipeline TA</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Mission</th><th>Client</th><th>Statut</th><th>IA responsable</th>
                  <th>TA assigné</th><th>Délai réponse</th><th>Profils</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTeam.slice(0, 25).map(ao => {
                  const aoId   = String(ao.ao_id || ao.id);
                  const isMine = String(ao.talent_acquisition_id) === myId;
                  const status = (ao.ao_avancement || '').toUpperCase();
                  const isAvailable = status === 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION' && !ao.talent_acquisition_id;
                  const meta   = STATUS_META[status] || { label: ao.ao_avancement, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                  return (
                    <tr key={aoId} style={{ cursor:'pointer' }} onClick={() => navigate(`/ao/${aoId}`)}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          {isMine && (
                            <span style={{ width:8, height:8, borderRadius:'50%', background:'#3B82F6', flexShrink:0, display:'inline-block' }} title="Votre mission"/>
                          )}
                          <div>
                            <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{ao.job_title || 'Sans titre'}</div>
                            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{ao.source_type}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize:'0.8125rem' }}>{ao.client || '—'}</td>
                      <td>
                        <span style={{ whiteSpace:'nowrap', padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:meta.bg, color:meta.color }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{ao.handler_user_name || '—'}</td>
                      <td style={{ fontSize:'0.8125rem' }}>
                        {ao.talent_acquisition_name
                          ? <span style={{ fontWeight:600, color:'#3B82F6' }}>{ao.talent_acquisition_name}</span>
                          : <span style={{ color:'var(--text-muted)' }}>Équipe</span>}
                      </td>
                      <td>
                        {ao.response_deadline
                          ? <DeadlineBadge deadline={ao.response_deadline}/>
                          : <span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>—</span>}
                      </td>
                      <td style={{ fontSize:'0.875rem', fontWeight:600, color:'#3B82F6' }}>
                        {ao.scored_candidates_count || 0}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        {isAvailable ? (
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={taking === aoId}
                            onClick={(e) => handleTakeSourcing(ao, e)}>
                            {taking === aoId ? '…' : <><Play size={12}/> Prendre</>}
                          </button>
                        ) : (
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/ao/${aoId}`)}>
                            <Eye size={13}/> Voir
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Missions clôturées */}
      {myClosedAOs.length > 0 && (
        <div className="card section">
          <div className="card-header">
            <span className="card-title">
              <CheckCircle size={15} style={{ verticalAlign:'middle', marginRight:6 }}/>
              Mes missions clôturées ({myClosedAOs.length})
            </span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Mission</th><th>Client</th><th>Statut</th><th>IA responsable</th><th>Profils</th><th></th></tr>
              </thead>
              <tbody>
                {myClosedAOs.map(ao => {
                  const aoId = String(ao.ao_id || ao.id);
                  const meta = STATUS_META[(ao.ao_avancement||'').toUpperCase()] || { label: ao.ao_avancement, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                  return (
                    <tr key={aoId} style={{ cursor:'pointer', opacity:0.8 }} onClick={() => navigate(`/ao/${aoId}`)}>
                      <td><span style={{ fontWeight:600, fontSize:'0.875rem' }}>{ao.job_title || 'Sans titre'}</span></td>
                      <td style={{ fontSize:'0.8125rem' }}>{ao.client || '—'}</td>
                      <td>
                        <span style={{ whiteSpace:'nowrap', padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:meta.bg, color:meta.color }}>
                          {meta.label}
                        </span>
                      </td>
                      <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{ao.handler_user_name || '—'}</td>
                      <td style={{ fontSize:'0.875rem', fontWeight:600, color:'#3B82F6' }}>{ao.scored_candidates_count || 0}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/ao/${aoId}`)}>
                          <Eye size={13}/> Voir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Composants partagés
// ─────────────────────────────────────────────────────────────
function AORow({ ao, isNew, onClick }) {
  const status = STATUS_META[(ao.ao_avancement||'').toUpperCase()] || { label: ao.ao_avancement, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
  return (
    <div onClick={onClick} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px',
      borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background='var(--gray-50)'}
      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:'0.875rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {ao.job_title || 'AO sans titre'}
        </div>
        <div style={{ display:'flex', gap:8, marginTop:3, fontSize:'0.75rem', color:'var(--text-muted)' }}>
          {ao.client   && <span>{ao.client}</span>}
          {ao.location && <span>· {ao.location}</span>}
          {ao.tjm      && <span style={{ color:'#EA580C', fontWeight:600 }}>· {ao.tjm}</span>}
        </div>
      </div>
      {isNew ? (
        <span style={{ whiteSpace:'nowrap', padding:'2px 8px', borderRadius:20, fontSize:'0.6875rem', fontWeight:700, background:'rgba(59,130,246,0.08)', color:'#3B82F6', border:'1px solid rgba(59,130,246,0.22)', flexShrink:0 }}>Nouveau</span>
      ) : (
        <span style={{ whiteSpace:'nowrap', padding:'2px 8px', borderRadius:20, fontSize:'0.6875rem', fontWeight:700, background:status.bg, color:status.color, flexShrink:0 }}>{status.label}</span>
      )}
      <ArrowRight size={14} color="var(--text-muted)"/>
    </div>
  );
}

function DeadlineBadge({ deadline }) {
  const today = new Date();
  const due   = new Date(deadline);
  const diff  = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (isNaN(diff)) return <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{deadline}</span>;
  const color = diff < 0 ? '#DC2626' : diff <= 2 ? '#F59E0B' : diff <= 7 ? '#D97706' : '#16A34A';
  const bg    = diff < 0 ? 'rgba(239,68,68,0.12)' : diff <= 2 ? '#FFFBEB' : diff <= 7 ? 'rgba(217,119,6,0.1)' : 'rgba(34,197,94,0.08)';
  return (
    <span style={{ whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:bg, color }}>
      <Calendar size={10}/>
      {diff < 0 ? 'Expiré' : diff === 0 ? "Auj." : `${diff}j restants`}
    </span>
  );
}
